import React, { useEffect, useRef } from "react";
import YouTube from "react-youtube";

function playBeep() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = "sine";
  osc.frequency.setValueAtTime(1000, ctx.currentTime);
  gain.gain.setValueAtTime(0.1, ctx.currentTime);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.1);
}

export default function VideoPlayer({
  videoId,
  bookmark,
  playerRef,
  loopEnabled,
  countInEnabled,
  onDuration,
  onReady,
  countdownOverlay,
  onLoopRestart,
  onLoaded,
  setCurrentTime,
  setIsPlaying,
}) {
  const intervalRef = useRef(null);   // loop boundary checker
  const timePollRef = useRef(null);   // currentTime poller

  const opts = {
    width: "100%",
    height: "100%",
    playerVars: {
      autoplay: 0,
      controls: 1,
      modestbranding: 1,
      rel: 0,
    },
  };

  const clearTimePoll = () => {
    if (timePollRef.current) {
      clearInterval(timePollRef.current);
      timePollRef.current = null;
    }
  };

  const handleReady = (event) => {
    playerRef.current = event.target;

    const checkDuration = () => {
      const duration = event.target.getDuration();
      if (duration > 0) {
        onDuration(duration);
        if (bookmark?.speed !== undefined) {
          event.target.setPlaybackRate(bookmark.speed);
        }
      } else {
        setTimeout(checkDuration, 100);
      }
    };
    checkDuration();

    // Start currentTime polling once player is ready
    clearTimePoll();
    if (typeof setCurrentTime === "function") {
      timePollRef.current = setInterval(() => {
        const time = event.target.getCurrentTime?.();
        if (typeof time === "number") {
          setCurrentTime(time);
        }
      }, 250);
    }

    onReady?.();
    onLoaded?.();
  };

  // ✅ NEW: keep React state in sync with YouTube player UI actions
  const handleStateChange = (e) => {
    // YT.PlayerState: -1 UNSTARTED, 0 ENDED, 1 PLAYING, 2 PAUSED, 3 BUFFERING, 5 CUED
    const s = e?.data;
    if (s === 1) {
      setIsPlaying?.(true);
    } else if (s === 0 || s === 2 || s === -1 || s === 3 || s === 5) {
      setIsPlaying?.(false);
    }
  };

  // Keep playback rate synced with bookmark.speed
  useEffect(() => {
    if (
      playerRef?.current &&
      typeof playerRef.current.setPlaybackRate === "function" &&
      bookmark?.speed !== undefined
    ) {
      playerRef.current.setPlaybackRate(bookmark.speed);
    }
  }, [bookmark?.speed, playerRef]);

  // Restart currentTime polling when videoId changes
  useEffect(() => {
    clearTimePoll();
    return () => clearTimePoll();
  }, [videoId]);

  // Loop boundary check / count-in logic
  useEffect(() => {
    if (!playerRef?.current) return;

    function checkPosition() {
      const currentTime = playerRef.current.getCurrentTime();
      if (
        !bookmark ||
        typeof bookmark.start !== "number" ||
        typeof bookmark.end !== "number" ||
        typeof currentTime !== "number"
      ) {
        return;
      }

      if (currentTime >= bookmark.end) {
        if (loopEnabled) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          const bpm = bookmark.bpm || 0;
          const beats = bookmark.leadInBeats || 0;

          if (countInEnabled && bpm > 0 && beats > 0) {
            const interval = 60000 / bpm;
            playerRef.current.pauseVideo();

            for (let i = 0; i < beats; i++) {
              setTimeout(() => playBeep(), i * interval);
            }

            onLoopRestart?.(beats, bpm);

            setTimeout(() => {
              playerRef.current.seekTo(bookmark.start);
              playerRef.current.playVideo();
              intervalRef.current = setInterval(checkPosition, 200);
              setIsPlaying?.(true);
            }, beats * interval);
          } else {
            playerRef.current.seekTo(bookmark.start);
            playerRef.current.playVideo();
            intervalRef.current = setInterval(checkPosition, 200);
            setIsPlaying?.(true);
          }
        } else {
          playerRef.current.pauseVideo();
          setIsPlaying?.(false);
        }
      }
    }

    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(checkPosition, 200);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookmark?.start,
    bookmark?.end,
    bookmark?.bpm,
    bookmark?.leadInBeats,
    loopEnabled,
    countInEnabled,
    onLoopRestart,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimePoll();
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  return (
    <div className="w-full max-w-3xl mx-auto mt-4">
      <div className="relative" style={{ paddingBottom: "56.25%", height: 0 }}>
        {/* ⏱ Countdown Overlay */}
        {countdownOverlay && (
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded font-bold z-20"
            style={countdownOverlay.style || {}}
          >
            {countdownOverlay.text}
          </div>
        )}

        <YouTube
          videoId={videoId}
          opts={opts}
          onReady={handleReady}
          onStateChange={handleStateChange}   // ✅ listen for play/pause/end from iframe
          onError={(e) => {
            console.error("YouTube player error:", e);
            alert(
              "There was an issue loading the video. Please try again or check if the video is playable."
            );
          }}
          className="absolute top-0 left-0 w-full h-full"
        />
      </div>
    </div>
  );
}
